import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, MousePointerClick, Users, TrendingUp } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsStats = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    const confirmMsg = lang === 'tr'
      ? 'Tüm ziyaret verileri kalıcı olarak silinecek. Emin misiniz?'
      : lang === 'es'
      ? '¿Está seguro? Se eliminarán TODOS los datos de analítica.'
      : 'This will permanently delete ALL analytics data. Are you sure?';
    if (!window.confirm(confirmMsg)) return;

    try {
      setResetting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/analytics/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAnalytics();
    } catch (error) {
      console.error('Error resetting analytics:', error);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  if (!stats) {
    return <div className="text-center py-20" style={{ color: 'var(--text-secondary)' }}>No analytics data available</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
          {t('admin.analytics')}
        </h2>
        <button
          onClick={handleReset}
          disabled={resetting}
          data-testid="reset-analytics-btn"
          className="px-4 py-2 rounded-md text-sm font-semibold border transition-colors disabled:opacity-50"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', background: 'var(--bg-secondary)' }}
        >
          {resetting
            ? (lang === 'tr' ? 'Siliniyor...' : lang === 'es' ? 'Borrando...' : 'Resetting...')
            : (lang === 'tr' ? 'Analitiği Sıfırla' : lang === 'es' ? 'Reiniciar Analítica' : 'Reset Analytics')}
        </button>
      </div>

      {/* Big Number Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="rounded-lg p-6 shadow-lg border-l-4 border-blue-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <Eye className="w-8 h-8 text-blue-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.total_visits}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.totalVisits')}</p>
        </div>

        <div className="rounded-lg p-6 shadow-lg border-l-4 border-green-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-green-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.unique_visitors}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.uniqueVisitors')}</p>
        </div>

        <div className="rounded-lg p-6 shadow-lg border-l-4 border-purple-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-purple-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.today_visits}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.todayVisits')}</p>
        </div>

        <div className="rounded-lg p-6 shadow-lg border-l-4 border-orange-500" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center justify-between mb-2">
            <MousePointerClick className="w-8 h-8 text-orange-500" />
            <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.avg_page_views}</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('admin.avgPageViews')}</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Traffic Sources Pie Chart */}
        <div className="rounded-lg p-6 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('admin.trafficSources')}
          </h3>
          <Doughnut
            data={{
              labels: [t('admin.direct'), t('admin.google'), t('admin.socialMedia'), t('admin.other')],
              datasets: [{
                data: [
                  stats.traffic_sources.direct,
                  stats.traffic_sources.google,
                  stats.traffic_sources.social,
                  stats.traffic_sources.other
                ],
                backgroundColor: [
                  'rgba(59, 130, 246, 0.8)',
                  'rgba(239, 68, 68, 0.8)',
                  'rgba(34, 197, 94, 0.8)',
                  'rgba(234, 179, 8, 0.8)'
                ]
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { position: 'bottom' }
              }
            }}
          />
        </div>

        {/* Popular Pages Bar Chart */}
        <div className="rounded-lg p-6 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('admin.popularPages')}
          </h3>
          <Bar
            data={{
              labels: stats.popular_pages.map(p => p.page),
              datasets: [{
                label: t('admin.visits'),
                data: stats.popular_pages.map(p => p.visits),
                backgroundColor: 'rgba(59, 130, 246, 0.7)'
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { display: false }
              },
              scales: {
                y: { beginAtZero: true }
              }
            }}
          />
        </div>
      </div>

      {/* 7-Day Trend Line Chart */}
      <div className="rounded-lg p-6 shadow-lg mb-8" style={{ background: 'var(--bg-primary)' }}>
        <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
          {t('admin.weeklyTrend')}
        </h3>
        <Line
          data={{
            labels: stats.last_7_days.map(d => new Date(d.date).toLocaleDateString()),
            datasets: [{
              label: t('admin.dailyVisits'),
              data: stats.last_7_days.map(d => d.visits),
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.4,
              fill: true
            }]
          }}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: { beginAtZero: true }
            }
          }}
        />
      </div>

      {/* Detailed Referrers Table */}
      <div className="rounded-lg shadow-md overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
        <div className="p-6">
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('admin.detailedReferrers')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.referrer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.visits')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {stats.detailed_referrers.map((ref, idx) => (
                <tr key={idx}>
                  <td className="px-6 py-4" style={{ color: 'var(--text-primary)' }}>
                    {ref.referrer}
                  </td>
                  <td className="px-6 py-4 font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {ref.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsStats;
